import { defaults } from 'imports';
import { deepAssign, deepClone } from 'utils';
import { checkAPIEnum, mergePagination } from 'api/utils';
import { ActionType, INEXPIRABLE_REDUCER_EXPIRATION_DATE, REDUCER_EXPIRATION_TIMEOUT, SESSION_EXPIRATION_TIMEOUT, RequestStatus } from 'utils/constants';

const SESSION_ENTITY_TYPE = defaults.SessionInformation.EntityType;

export function combineReducers(reducersStructure, variableNames) {
    const keys = [];
    for (let key in reducersStructure) {
        if (reducersStructure.hasOwnProperty(key)) {
            const reducer = reducersStructure[key];
            if (key[0] !== '@' || (key = variableNames[key.slice(1)])) {
                keys.unshift({ key: key, reducer: reducer });
            }
        }
    }

    return function combinedReducers(state, action) {
        if (state === null) {
            return state;
        }
        state = state || {};
        const isArray = Array.isArray(state),
            newState = Object.assign(isArray ? [] : {}, state);
        let changed = false,
            i = keys.length; // https://jsperf.com/fastest-array-loops-in-javascript/32
        while (i--) {
            let key = keys[i].key, reducer = keys[i].reducer;
            if (typeof key === 'function') {
                key = key(state, action);
            }
            if (key !== undefined) {
                const newStatePart = reducer(state[key], action);
                if (newStatePart !== null) {
                    if (state[key] !== newStatePart) {
                        newState[key] = newStatePart;
                        changed = true;
                    }
                }
                else {
                    if (state[key]) {
                        if (isArray) {
                            newState.splice(key, 1);
                        }
                        else {
                            delete newState[key];
                        }
                        changed = true;
                    }
                }
            }

        }
        return changed ? newState : state;
    };
}

export function pipeReducers(...reducers) {
    reducers = reducers.reverse();
    return function pipedReducers(state, action) {
        let i = reducers.length; // https://jsperf.com/foreach-vs-reduce-vs-for-loop,  https://jsperf.com/fastest-array-loops-in-javascript/32
        while (i--) {
            state = reducers[i](state, action);
        }
        return state;
    }
}

/**
 * Build actual reducers from shortened structure:
 * [reducer1, reducer2, reducer3] -> pipeReducers(reducer1, reducer2, reducer3)
 * { field1: reducer1, field2: reducer2 } -> combineReducers({ field1: reducer1, field2: reducer2 }, keyVariables)
 * any function will be preserved, any nested object will be built recursively
 * @param {any} reducers
 * @param {object} keyVariables
 */
export function buildReducers(reducers, keyVariables) {
    switch (true) {
        case typeof reducers === 'function':
            return reducers;
        case Array.isArray(reducers):
            return pipeReducers(...reducers.map(r => buildReducers(r, keyVariables)));
        case typeof reducers === 'object' && !!reducers:
            for (let i in reducers) {
                if (reducers.hasOwnProperty(i)) {
                    reducers[i] = buildReducers(reducers[i], keyVariables);
                }
            }
            return combineReducers(reducers, keyVariables);
        default:
            throw new Error(`Unknown type of reducers: ${reducers}`);
    }
}

export function assignmentReducer(actionType, def, getValue) {
    if (DEBUG && !actionType) {
        throw new Error('No action to handle.');
    }
    if (DEBUG && !getValue) {
        throw new Error('Can not extract value from action.');
    }
    return function assignmentReducer(state, action) {
        return action.type === actionType ?
            (typeof getValue === 'string' ? action[getValue] : getValue(state, action)) :
            (state === undefined ? def : state)
    }
}

function isSessionState(state) {
    return state !== undefined && state !== null && Object.prototype.hasOwnProperty.call(state, 'EntityType') && state.EntityType === SESSION_ENTITY_TYPE;
}

export function networkActionReducer(prefix, def) {
    if (DEBUG && !prefix) {
        throw new Error('No action prefix to handle.');
    }
    return function networkActionReducer(state = deepClone(def || null), action) {
        if (state === null) {
            return state;
        }

        switch (action.type) {
            case ActionType[`${prefix}_PENDING`]:
                return deepAssign(state, { Custom: { status: RequestStatus.PENDING, networkActionType: prefix } });

            case ActionType[`${prefix}_SUCCESS`]:
                const
                    isSession = isSessionState(state),
                    expirationDate = new Date(+new Date() + (isSession ? SESSION_EXPIRATION_TIMEOUT : REDUCER_EXPIRATION_TIMEOUT));

                return deepAssign(state, { Custom: { status: RequestStatus.SUCCEEDED, expires: expirationDate, networkActionType: prefix } });

            case ActionType[`${prefix}_FAIL`]:
                return deepAssign(state, { Custom: { status: RequestStatus.FAILED, expires: new Date(), networkActionType: prefix } });

            default:
                return state;
        }
    }
}

export function networkEntityLoadReducer(prefix, def) {
    if (DEBUG && !prefix) {
        throw new Error('No action prefix to handle.');
    }
    const nr = networkActionReducer(prefix, def)
    return function networkEntityLoadReducer(state, action) {
        state = nr(state, action);

        if (action.type === ActionType[`${prefix}_SUCCESS`]) {
            const
                isSession = isSessionState(state),
                expirationDate = new Date(+new Date() + (isSession ? SESSION_EXPIRATION_TIMEOUT : REDUCER_EXPIRATION_TIMEOUT));

            state = action.response ? deepAssign(action.response, {
                Custom: {
                    status: RequestStatus.SUCCEEDED,
                    expires: expirationDate,
                    networkActionType: prefix,
                    internalID: (state || action.response).Custom.internalID
                }
            }) : null;
        }

        return state;
    }
}

export function networkObjectLoadReducer(prefix, def) {
    if (DEBUG && !prefix) {
        throw new Error('No action prefix to handle.');
    }
    const nr = networkActionReducer(prefix, def)
    return function networkObjectLoadReducer(state, action) {
        state = nr(state, action);

        if (action.type === ActionType[`${prefix}_SUCCESS`]) {
            state = action.response !== null ? Object.assign({}, state, { Data: action.response }) : null;
        }

        return state;
    }
}

export function customExpirationNetworkObjectLoadReducer(prefix, def, expirationTimeout) {
    if (DEBUG && !prefix) {
        throw new Error('No action prefix to handle.');
    }

    const nr = networkObjectLoadReducer(prefix, def);
    return function customExpirationNetworkObjectLoadReducer(state, action) {
        state = nr(state, action);

        if (action.type === ActionType[`${prefix}_SUCCESS`] && !isSessionState(state)) {
            const expirationDate = new Date(+new Date() + expirationTimeout);
            return deepAssign(state, { Custom: { expires: expirationDate } });
        }

        return state;
    }
}

export function networkObjectTrackChangesReducer(relatedEntityTypes, typeRenameMap) {
    if (DEBUG && (!relatedEntityTypes || !relatedEntityTypes.length)) {
        throw new Error('No entities to handle.');
    }
    return function networkObjectTrackChangesReducer(state, action) {
        if (!state) {
            return state;
        }

        // TODO?
        // one change may cause different consequences at different points in time
        // for example rename of dashboard page which is not currently active should cause silent refresh of top menu immediately
        // but there is no need to reload actual page data until page is shown or if it has not been loaded yet
        // if first one would not drop change or mark it as processed in some other way, it wouldn't know if it did reload titles or not
        // and if it would drop it then we couldn't know if page is invalid when user wants to see it
        // so we need to distinguish same change in different parts of store
        // clone change object and assume that different object instances mean different parts?
        // add some scope field?
        // for now just rename entity type
        const change = action.change ?
            (typeRenameMap ? deepAssign(action.change, { EntityType: typeRenameMap[action.change.EntityType] || action.change.EntityType }) : action.change) :
            null;

        if (action.type === ActionType.ADD_ENTITY_CHANGE && relatedEntityTypes.includes(change.EntityType)) {
            return Object.assign({}, state, {
                Custom: Object.assign({}, state.Custom, {
                    expires: new Date(),
                    changes: state.Custom.changes.concat([change])
                })
            });
        }

        if (action.type === ActionType.DROP_ENTITY_CHANGE && relatedEntityTypes.includes(change.EntityType)) {
            return Object.assign({}, state, {
                Custom: Object.assign({}, state.Custom, {
                    expires: new Date(new Date() + REDUCER_EXPIRATION_TIMEOUT),
                    changes: state.Custom.changes.filter(c => change.ChangeType !== c.ChangeType || change.EntityID !== c.EntityID)
                })
            });
        }

        return state;
    }
}

export function networkObjectVoidReducer(actionType, resetTo) {
    if (DEBUG && !actionType) {
        throw new Error('No action type to handle.');
    }
    return function networkObjectVoidReducer(state, action) {
        if (action.type === actionType) {
            state = resetTo || deepAssign(state, {
                Custom:
                    action.immediately === undefined || action.immediately === false ?
                        { expires: new Date() } :
                        { status: RequestStatus.UNSYNCED, changes: [] }
            });
        }

        return state;
    }
}

// TODO merge this stuff with networkEntityLoadReducer
// pass no default value for current networkEntityLoadReducer behaviour and some value for optimistic one
export function optimisticNetworkEntityLoadReducer(prefix, def, getID) {
    if (DEBUG && !prefix) {
        throw new Error('No action prefix to handle.');
    }
    if (DEBUG && !getID) {
        throw new Error('Can not extract ID from action.');
    }
    const nr = networkActionReducer(prefix, null);
    return function optimisticNetworkEntityLoadReducer(state, action) {
        switch (action.type) {
            case ActionType[`${prefix}_PENDING`]:
                const isPlaceholder = !state;
                state = state || deepClone(def || null);
                return deepAssign(nr(state, action), { ID: getID(state, action), Custom: { isOptimisticPlaceholder: isPlaceholder } });

            case ActionType[`${prefix}_SUCCESS`]:
                return state ? (action.response ? deepAssign(action.response, { Custom: { ...nr(state, action).Custom, isOptimisticPlaceholder: false } }) : null) : state;

            default:
                return state ? nr(state, action) : state;
        }
    }
}

export function networkPaginationLoadReducer(prefix, def) {
    if (DEBUG && !prefix) {
        throw new Error('No action prefix to handle.');
    }
    const nr = networkEntityLoadReducer(prefix, def);
    return function networkPaginationLoadReducer(state, action) {
        if (action.type === ActionType[`${prefix}_PENDING`] && action.reset) {
            state = def;
        }
        if (state && action.type === ActionType[`${prefix}_SUCCESS`] && action.response) {
            mergePagination(state, action.response);
        }
        return nr(state, action);
    };
}

export function networkEntityAddAllReducer(prefix, def, getValues) {
    if (DEBUG && !prefix) {
        throw new Error('No action prefix to handle.');
    }
    if (DEBUG && !getValues) {
        throw new Error('Can not extract entity from action.');
    }
    const nr = networkActionReducer(prefix, def);
    return function networkEntityAddAllReducer(state, action) {
        switch (action.type) {
            case ActionType[prefix]:
                return deepAssign(state, [...state, ...getValues(state, action)]);

            case ActionType[`${prefix}_PENDING`]:
            case ActionType[`${prefix}_FAIL`]:
                return deepAssign(state, state.map(item => getValues(state, action).getByID(item.ID) ? nr(item, action) : item));

            case ActionType[`${prefix}_SUCCESS`]:
                let index;
                return deepAssign(state, state.map(item =>
                    (index = getValues(state, action).getIndexByID(item.ID)) !== -1 ?
                        typeof action.response[index] !== 'object' ?
                            nr({ ID: action.response[index], Custom: { internalID: item.ID } }, action) :
                            nr(action.response[index], action) :
                        item
                ));

            default:
                return state;
        }
    }
}

export function networkEntityAddReducer(prefix, def, getValue) {
    if (DEBUG && !prefix) {
        throw new Error('No action prefix to handle.');
    }
    if (DEBUG && !getValue) {
        throw new Error('Can not extract entity from action.');
    }
    const nr = networkActionReducer(prefix, def);
    return function networkEntityAddReducer(state, action) {
        state = nr(state, action);

        switch (action.type) {
            case ActionType[prefix]:
                return getValue(state, action);

            case ActionType[`${prefix}_SUCCESS`]:
                return state && deepAssign(state, typeof action.response !== 'object' ? { ID: action.response, Custom: { internalID: state.ID } } : action.response);

            default:
                return state;
        }
    }
}

export function networkEntityEditAllReducer(prefix, def, getValues, reassignAfterNA) {
    if (DEBUG && !prefix) {
        throw new Error('No action prefix to handle.');
    }
    if (DEBUG && !getValues) {
        throw new Error('Can not extract entity from action.');
    }
    const nr = networkActionReducer(prefix, def);
    return function networkEntityAddAllReducer(state, action) {
        switch (action.type) {
            case ActionType[prefix]:
                return deepAssign(state, state.map(item => ({ ...item, ...getValues(state, action).getByID(item.ID) })));

            case ActionType[`${prefix}_PENDING`]:
            case ActionType[`${prefix}_FAIL`]:
                return deepAssign(state, state.map(item => getValues(state, action).getByID(item.ID) ? nr(item, action) : item));

            case ActionType[`${prefix}_SUCCESS`]:
                return deepAssign(state, state.map(item =>
                    reassignAfterNA ?
                        nr(getValues(state, action).getByID(item.ID) || item, action) :
                        nr(item, action)
                ));

            default:
                return state;
        }
    }
}

export function networkEntityEditReducer(prefix, def, getValue, reassignAfterNA) {
    if (DEBUG && !prefix) {
        throw new Error('No action prefix to handle.');
    }
    if (DEBUG && !getValue) {
        throw new Error('Can not extract entity from action.');
    }
    const nr = networkActionReducer(prefix, def);
    return function networkEntityEditReducer(state, action) {
        switch (action.type) {
            case ActionType[prefix]:
                return { ...state, ...getValue(state, action) };

            case ActionType[prefix + '_SUCCESS']:
                return reassignAfterNA ?
                    nr(getValue(state, action), action) :
                    nr(state, action);

            default:
                return nr(state, action);
        }
    }
}

export function networkEntityDeleteAllReducer(prefix, def, getIDs) {
    if (DEBUG && !prefix) {
        throw new Error('No action prefix to handle.');
    }
    if (DEBUG && !getIDs) {
        throw new Error('Can not extract IDs of entities from action.');
    }
    const nr = networkActionReducer(prefix, def);
    return function networkEntityDeleteAllReducer(state, action) {
        switch (action.type) {
            case ActionType[`${prefix}_PENDING`]:
            case ActionType[`${prefix}_FAIL`]:
                return deepAssign(state, state.map(item => getIDs(state, action).includes(item.ID) ? nr(item, action) : item));

            case ActionType[`${prefix}_SUCCESS`]:
                let index;
                return deepAssign(state, state.filter(item => index = !getIDs(state, action).includes(item.ID)));

            default:
                return state;
        }
    }
}

export function networkEntityDeleteReducer(prefix, def) {
    if (DEBUG && !prefix) {
        throw new Error('No action prefix to handle.');
    }
    const nr = networkActionReducer(prefix, def);
    return function networkEntityDeleteReducer(state, action) {
        return action.type === ActionType[`${prefix}_SUCCESS`] ? null : nr(state, action);
    }
}

export function paginationSlicePushReducer(actionType, def) {
    if (DEBUG && !actionType) {
        throw new Error('No action to handle.');
    }
    return function paginationSlicePushReducer(state, action) {
        state = state || def || defaults.NetworkArray;

        if (action.type === actionType) {
            return deepAssign(state, { Count: state.Count + 1, TotalCount: state.TotalCount + 1 });
        }

        return state;
    }
}

export function paginationSlicePopReducer(actionType, def) {
    if (DEBUG && !actionType) {
        throw new Error('No action to handle.');
    }
    return function paginationSlicePopReducer(state, action) {
        state = state || def || defaults.NetworkArray;

        if (action.type === actionType) {
            return deepAssign(state, { Count: state.Count - 1, TotalCount: state.TotalCount - 1 });
        }

        return state;
    }
}

export function synchronizableEnumReducer(prefix, def, enumeration, tableName) {
    if (DEBUG && !prefix) {
        throw new Error('No action prefix to handle.');
    }
    if (DEBUG && !enumeration) {
        throw new Error('No local enumeration to synchronize with.');
    }
    const nr = networkObjectLoadReducer(prefix, def);
    return function synchronizableEnumReducer(state, action) {
        state = nr(state, action);

        if (action.type === ActionType[`${prefix}_SUCCESS`]) {
            checkAPIEnum(enumeration, action.response, tableName);
        }

        if (Object.prototype.hasOwnProperty.call(state, 'Custom') && +state.Custom.expires < +INEXPIRABLE_REDUCER_EXPIRATION_DATE) {
            state = deepAssign(state, { Custom: { expires: INEXPIRABLE_REDUCER_EXPIRATION_DATE } });
        }

        return state;
    };
}

export function sortableEntitiesLifecycleReducer(groupByPropertyName = "", reducersStructure, keyVariables) {
    if (DEBUG && !reducersStructure) {
        throw new Error('No nested reducers. This reducer handles changes in nested entities, it will do nothing without other reducers.');
    }
    const cr = typeof reducersStructure !== 'function' ? combineReducers(reducersStructure, keyVariables) : reducersStructure;
    return function sortableEntitiesLifecycleReducer(state, action) {
        const oldState = state;
        const newState = cr(state, action);

        return !oldState || newState === oldState ?
            newState :
            deepAssign(newState, newState
                .group(entity => entity[groupByPropertyName])
                .sortBy(group => group.key)
                .map(group => {
                    const
                        set = group
                            .filter(entity => entity.Sort && oldState.getByID(entity.ID) !== newState.getByID(entity.ID))
                            .sortBy(entity => entity.Sort),
                        unset = group
                            .filter(entity => !entity.Sort || oldState.getByID(entity.ID) === newState.getByID(entity.ID))
                            .sortBy(entity => entity.Sort || (group.length - set.length) + Math.abs(entity.ID));

                    let autoOffset = 0;

                    return [...unset, ...set]
                        .map((_, index) => set.find(entity => entity.Sort === index + 1) || unset[autoOffset++])
                        .concat(set.filter(entity => entity.Sort > unset.length + set.length))
                        .map((entity, index) => deepAssign(entity, { Sort: index + 1 }));
                })
                .flatten()
            );
    }
}

export function nestableEntitiesLifecycleReducer(reducersStructure, keyVariables) {
    if (DEBUG && !reducersStructure) {
        throw new Error('No nested reducers. This reducer handles changes in nested entities, it will do nothing without other reducers.');
    }
    const cr = combineReducers(reducersStructure, keyVariables);
    return function nestableEntitiesLifecycleReducer(state, action) {
        const oldState = state;
        const newState = cr(state, action);

        if (!oldState || newState === oldState) {
            return newState;
        }

        if (Math.abs(newState.length - oldState.length) > 1 || newState.filter(item => !oldState.includes(item)) > 1) {
            throw new Error("Can not recalculate Sort/ScopeEndSort, more than one entity were changed");
        }

        if (newState.length > oldState.length) {
            const newEntity = newState.find(entity => !oldState.getByID(entity.ID));
            const newParentScope = newEntity.FatherID ? state.getByID(newEntity.FatherID) : { DepthLevel: 0, Sort: 0, ScopeEndSort: oldState.length };
            const count = 1;

            return deepAssign(newState, newState
                .map(entity => deepAssign(entity,
                    entity.ID === newEntity.ID ? { Sort: newParentScope.ScopeEndSort + 1, ScopeEndSort: newParentScope.ScopeEndSort + count, DepthLevel: newParentScope.DepthLevel + 1 } :
                        entity.Sort <= newParentScope.Sort && entity.ScopeEndSort >= newParentScope.ScopeEndSort ? { ScopeEndSort: entity.ScopeEndSort + count } :
                            entity.Sort > newParentScope.ScopeEndSort ? { Sort: entity.Sort + count, ScopeEndSort: entity.ScopeEndSort + count } :
                                entity))
                .sortBy(({ Sort }) => Sort));
        }
        else if (newState.length === oldState.length && newState !== oldState) {
            const newEntity = newState.find(entity => !oldState.includes(entity));
            const oldEntity = oldState.find(entity => !newState.includes(entity));
            if (newEntity.ID === oldEntity.ID && newEntity.FatherID !== oldEntity.FatherID) {
                const newParentScope = newEntity.FatherID ? state.getByID(newEntity.FatherID) : { DepthLevel: 0, Sort: 0, ScopeEndSort: oldState.length };
                const count = oldEntity.ScopeEndSort - oldEntity.Sort + 1;
                const insertGapOffset = newParentScope.ScopeEndSort < oldEntity.Sort ? count : 0;
                const scopeAfterInsertGap = { Sort: oldEntity.Sort + insertGapOffset, ScopeEndSort: oldEntity.ScopeEndSort + insertGapOffset };
                const offset = newParentScope.ScopeEndSort + 1 - scopeAfterInsertGap.Sort;

                return deepAssign(newState, newState
                    .map(entity => deepAssign(entity,
                        entity.Sort <= newParentScope.Sort && entity.ScopeEndSort >= newParentScope.ScopeEndSort ? { ScopeEndSort: entity.ScopeEndSort + count } :
                            entity.Sort > newParentScope.ScopeEndSort ? { Sort: entity.Sort + count, ScopeEndSort: entity.ScopeEndSort + count } :
                                entity))
                    .map(entity => deepAssign(entity,
                        entity.Sort >= scopeAfterInsertGap.Sort && entity.ScopeEndSort <= scopeAfterInsertGap.ScopeEndSort ?
                            { Sort: entity.Sort + offset, ScopeEndSort: entity.ScopeEndSort + offset, DepthLevel: newParentScope.DepthLevel + 1 } :
                            entity))
                    .map(entity => deepAssign(entity,
                        entity.Sort <= scopeAfterInsertGap.Sort && entity.ScopeEndSort >= scopeAfterInsertGap.ScopeEndSort ? { ScopeEndSort: entity.ScopeEndSort - count } :
                            entity.Sort > scopeAfterInsertGap.ScopeEndSort ? { Sort: entity.Sort - count, ScopeEndSort: entity.ScopeEndSort - count } :
                                entity))
                    .sortBy(({ Sort }) => Sort));
            }
            else {
                return newState;
            }
        }
        else if (newState.length < oldState.length) {
            const oldEntity = oldState.find(entity => !newState.getByID(entity.ID));
            const count = oldEntity.ScopeEndSort - oldEntity.Sort + 1;

            return deepAssign(newState, newState
                .filter(entity => entity.Sort < oldEntity.Sort || entity.ScopeEndSort > oldEntity.ScopeEndSort)
                .map(entity => deepAssign(entity,
                    entity.Sort <= oldEntity.Sort && entity.ScopeEndSort >= oldEntity.ScopeEndSort ? { ScopeEndSort: entity.ScopeEndSort - count } :
                        entity.Sort > oldEntity.ScopeEndSort ? { Sort: entity.Sort - count, ScopeEndSort: entity.ScopeEndSort - count } :
                            entity
                )));
        }
        else {
            return newState;
        }
    }
}